import { DeviceCalendar } from "@/lib/capacitor/calendar";
import { logger } from "@/logger";
import { type CalendarLink, writeCalendarLink } from "./calendar-link-store";
import { type ReconcileWindow, reconcile, toKnownEvents } from "./reconcile";
import { type ShiftForCalendar, toDesiredEvent } from "./shift-events";

const DAY = 24 * 60 * 60 * 1000;

/**
 * Hetzelfde venster als de ICS-feed in de api: 30 dagen terug, 180 vooruit. Beide wegen horen
 * hetzelfde te beloven, anders hangt het van je koppeling af hoe ver je agenda vooruit kijkt.
 */
export const CALENDAR_WINDOW_DAYS_BACK = 30;
export const CALENDAR_WINDOW_DAYS_AHEAD = 180;

export function calendarWindow(now: number = Date.now()): ReconcileWindow {
  return {
    from: now - CALENDAR_WINDOW_DAYS_BACK * DAY,
    to: now + CALENDAR_WINDOW_DAYS_AHEAD * DAY,
  };
}

/**
 * Ruimer dan het synchronisatievenster: bij "koppeling verbreken" moeten ook de afspraken weg die
 * inmiddels buiten het venster gevallen zijn. Anders laat het verbreken een staart achter die
 * niemand meer kan opruimen, want de app kijkt er nooit meer naar.
 */
const CLEANUP_WINDOW_DAYS = 730;

export interface SyncResult {
  created: number;
  updated: number;
  removed: number;
}

const logCalendar = logger.getLogger("calendar-sync");

/**
 * Trekt de gekoppelde agenda gelijk met de diensten.
 *
 * Alles wat we schrijven draagt onze handtekening, en alles wat we verwijderen is via die
 * handtekening als van ons herkend — zie `toKnownEvents`. Een mislukte losse afspraak stopt de rest
 * niet: liever negen kloppende afspraken en een logregel dan een halve agenda.
 */
export async function syncShiftsToCalendar(options: {
  shifts: ShiftForCalendar[];
  link: CalendarLink;
  now?: number;
}): Promise<SyncResult> {
  const { shifts, link } = options;
  const window = calendarWindow(options.now);

  const desired = shifts
    .map(toDesiredEvent)
    .filter((event): event is NonNullable<typeof event> => event !== null);

  const onDevice = await DeviceCalendar.listEventsInRange(window.from, window.to);
  const known = toKnownEvents(onDevice, link.calendarId);

  const plan = reconcile({ desired, known, window });

  const result: SyncResult = { created: 0, updated: 0, removed: 0 };

  for (const event of plan.create) {
    try {
      await DeviceCalendar.createEvent({
        calendarId: link.calendarId,
        title: event.title,
        location: event.location ?? undefined,
        description: event.description,
        startDate: event.startDate,
        endDate: event.endDate,
      });
      result.created += 1;
    } catch (error) {
      logCalendar.error("Afspraak aanmaken mislukt", { error, shiftId: event.shiftId });
    }
  }

  for (const { eventId, event } of plan.update) {
    try {
      await DeviceCalendar.modifyEvent({
        id: eventId,
        title: event.title,
        location: event.location ?? undefined,
        description: event.description,
        startDate: event.startDate,
        endDate: event.endDate,
      });
      result.updated += 1;
    } catch (error) {
      logCalendar.error("Afspraak bijwerken mislukt", { error, shiftId: event.shiftId });
    }
  }

  for (const eventId of plan.remove) {
    try {
      await DeviceCalendar.deleteEvent(eventId);
      result.removed += 1;
    } catch (error) {
      logCalendar.error("Afspraak verwijderen mislukt", { error, eventId });
    }
  }

  await writeCalendarLink({ ...link, lastSyncedAt: new Date().toISOString() });

  return result;
}

/**
 * Haalt onze afspraken weer uit de agenda. Wat geen handtekening van ons draagt blijft staan — dat
 * is van de medewerker zelf.
 */
export async function removeShiftsFromCalendar(
  link: CalendarLink,
  now: number = Date.now(),
): Promise<number> {
  const from = now - CLEANUP_WINDOW_DAYS * DAY;
  const to = now + CLEANUP_WINDOW_DAYS * DAY;

  const onDevice = await DeviceCalendar.listEventsInRange(from, to);
  const ours = toKnownEvents(onDevice, link.calendarId);

  let removed = 0;
  for (const event of ours) {
    try {
      await DeviceCalendar.deleteEvent(event.eventId);
      removed += 1;
    } catch (error) {
      logCalendar.error("Afspraak opruimen mislukt", { error, eventId: event.eventId });
    }
  }

  return removed;
}
